import i18n from 'i18next';
import { fileSizeValidation, fileTypeValidation } from './fileValidation-helpers';
import { startOfDay, startOfToday, startOfTomorrow, toDate } from 'date-fns';
import { convertDateToString, DateFormats, isValidDate, localFormat, toDateOrNull } from './date-helpers';
import { isNullOrEmpty } from './string-helper';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';
import {
  ValidationOptions,
  ValidationProps,
} from 'src/components/formEditor/toolbox/settingsPanel/content/Validation.component';
import { FieldSkipReason } from 'src/services/api/api-client';
import { isNumber } from 'lodash';

export const ValidationConstants = {
  textInputMaxLength: 120,
  minPersonMeasureValue: 1,
  maxWeightValue: 999,
  maxHeightValue: 250,
  maxCATValue: 40,
  minPatientId: 1,
  maxPatientId: 999,
  minAnalysisQuantityLimit: 1,
};

const isValidationRulePresent = (validationSettings: ValidationProps, field: keyof ValidationProps) => {
  const validationRules = Object.keys(validationSettings);

  return (
    validationRules.includes(field) &&
    validationSettings[field] !== null &&
    validationSettings[field] !== undefined &&
    validationSettings[field] !== false
  );
};

const replacePattern = (str: string | undefined | null, value: number | string | undefined | null) => {
  if (!value) return str;

  return str?.replace(
    '@value',
    isNumber(value) ? value?.toString() : localFormat(toDateOrNull(value)!, DateFormats.shortDate),
  );
};

export const ValidateCustomForm = ({
  validationSettings,
  value,
  skipReason,
  options,
}: {
  validationSettings: ValidationProps;
  value?: string | string[] | null | number | AppAttachmentValue[];
  skipReason?: FieldSkipReason;
  options?: ValidationOptions;
}): string[] => {
  if (options?.disable) return [];
  const messages: string[] = [];

  const fieldIsEmpty =
    !value || (typeof value === 'string' && isNullOrEmpty(value)) || (Array.isArray(value) && value.length === 0);

  if (!options?.ignoreIsRequiredRule && isValidationRulePresent(validationSettings, 'isRequired')) {
    if (fieldIsEmpty && skipReason === undefined) {
      messages.push(validationSettings.isRequiredErrorMessage || i18n.t('LocalErrors.FieldRequired'));
    }
  }

  if (options?.eCRF && fieldIsEmpty && skipReason === FieldSkipReason.NoReason) {
    messages.push(i18n.t('LocalErrors.ChoseSkipReason'));
  }

  if (isValidationRulePresent(validationSettings, 'minValue')) {
    // @ts-ignore
    if (value && value < validationSettings.minValue)
      messages.push(
        replacePattern(validationSettings.minValueErrorMessage, validationSettings.minValue) ||
          i18n.t('LocalErrors.MinValue', {
            minValue: validationSettings.minValue,
          }),
      );
  }

  if (isValidationRulePresent(validationSettings, 'maxValue')) {
    // @ts-ignore
    if (value && value > validationSettings.maxValue)
      messages.push(
        replacePattern(validationSettings.maxValueErrorMessage, validationSettings.maxValue) ||
          i18n.t('LocalErrors.MaxValue', {
            maxValue: validationSettings.maxValue,
          }),
      );
  }

  if (isValidationRulePresent(validationSettings, 'maxLength')) {
    // @ts-ignore
    if (value && typeof value === 'string' && value.length > validationSettings.maxLength) {
      messages.push(
        replacePattern(validationSettings.maxLengthErrorMessage, validationSettings.maxLength) ||
          i18n.t('LocalErrors.LengthIsExceeded'),
      );
    }
  }

  if (isValidationRulePresent(validationSettings, 'pattern')) {
    if (value && validationSettings.pattern) {
      const regexValue = new RegExp(validationSettings.pattern);
      const checkRegexRule = (val: string) => {
        if (!regexValue.test(val)) {
          messages.push(validationSettings.patternErrorMessage || i18n.t('LocalErrors.InvalidPattern'));
        }
      };

      if (Array.isArray(value)) {
        value.forEach((x) => checkRegexRule(x.toString()));
      } else {
        checkRegexRule(value.toString());
      }
    }
  }

  if (
    isValidationRulePresent(validationSettings, 'minDate') &&
    !isValidationRulePresent(validationSettings, 'minDateAsToday')
  ) {
    if (value && typeof value === 'string') {
      const valueAsDate = new Date(Date.parse(value));
      const validationValueAsDate = new Date(Date.parse(validationSettings.minDate!));
      if (isValidDate(validationValueAsDate) && isValidDate(valueAsDate)) {
        if (toDate(valueAsDate) < toDate(validationValueAsDate)) {
          // @ts-ignore
          messages.push(
            replacePattern(validationSettings.minDateErrorMessage, validationSettings.minDate) ||
              i18n.t('LocalErrors.DateIsTooSmall').toString(),
          );
        }
      }
    }
  }

  if (
    isValidationRulePresent(validationSettings, 'maxDate') &&
    !isValidationRulePresent(validationSettings, 'maxDateAsToday')
  ) {
    if (value && typeof value === 'string') {
      const valueAsDate = new Date(Date.parse(value));
      const validationValueAsDate = new Date(Date.parse(validationSettings.maxDate!));
      if (isValidDate(validationValueAsDate) && isValidDate(valueAsDate)) {
        if (toDate(valueAsDate) > toDate(validationValueAsDate)) {
          // @ts-ignore
          messages.push(
            replacePattern(validationSettings.maxDateErrorMessage, validationSettings.maxDate) ||
              i18n.t('LocalErrors.DateIsTooBig').toString(),
          );
        }
      }
    }
  }

  if (isValidationRulePresent(validationSettings, 'minDateAsToday')) {
    const minDate = startOfToday();
    if (value && typeof value === 'string') {
      const valueAsDate = new Date(Date.parse(value));
      if (isValidDate(minDate) && isValidDate(valueAsDate)) {
        if (startOfDay(toDate(valueAsDate)) < startOfDay(toDate(minDate))) {
          // @ts-ignore
          messages.push(
            replacePattern(validationSettings.minDateErrorMessage, validationSettings.minDate) ||
              i18n.t('LocalErrors.DateIsTooSmall').toString(),
          );
        }
      }
    }
  }

  if (isValidationRulePresent(validationSettings, 'maxDateAsToday')) {
    const maxDate = startOfToday();
    if (value && typeof value === 'string') {
      const valueAsDate = new Date(Date.parse(value));
      if (isValidDate(maxDate) && isValidDate(valueAsDate)) {
        if (startOfDay(toDate(valueAsDate)) > startOfDay(toDate(maxDate))) {
          // @ts-ignore
          messages.push(
            replacePattern(validationSettings.maxDateErrorMessage, convertDateToString(maxDate)) ||
              i18n.t('LocalErrors.DateIsTooBig').toString(),
          );
        }
      }
    }
  }

  if (Array.isArray(value) && typeof value[0] !== 'string' && value.some((x) => !!(x as AppAttachmentValue)?.file)) {
    const fileValidationResult = ValidationFormRules().filesValidate(value as AppAttachmentValue[]);
    if (fileValidationResult) {
      messages.push(fileValidationResult);
    }
  }

  return messages;
};

export const ValidationDateRange = (startDate: string, endDate: string): string => {
  if (startDate && typeof startDate === 'string' && endDate && typeof endDate === 'string') {
    const start = new Date(Date.parse(startDate));
    const end = new Date(Date.parse(endDate));

    if (isValidDate(start) && isValidDate(end) && toDate(start) > toDate(end)) {
      return i18n.t('LocalErrors.WrongDateRange');
    }
  }

  return '';
};

export const ValidationFormRules = () => {
  return {
    requiredRule: {
      required: {
        value: true,
        message: i18n.t('LocalErrors.FieldRequired'),
      },
    },
    maxLengthRule: {
      maxLength: {
        value: ValidationConstants.textInputMaxLength,
        message: i18n.t('LocalErrors.LengthIsExceeded'),
      },
    },
    deviceSerialNumberRule: {
      pattern: {
        value: /^[0-9\b]+$/,
        message: i18n.t('LocalErrors.DeviceFormat'),
      },
    },
    patientIdRule: {
      pattern: {
        value: /^[0-9\b]+$/,
        message: i18n.t('LocalErrors.PatientIdFormat'),
      },
      min: {
        value: ValidationConstants.minPatientId,
        message: i18n.t('ServerErrors.wrong_patient_uniq_id'),
      },
      max: {
        value: ValidationConstants.maxPatientId,
        message: i18n.t('ServerErrors.wrong_patient_uniq_id'),
      },
    },
    digitsOnlyRule: {
      pattern: {
        value: /^[0-9\b]+$/,
        message: i18n.t('LocalErrors.Digits'),
      },
    },
    bodyMeasurementRule: {
      pattern: {
        value: /^(?=[1-9])\d*\.?\d+$/gm,
        message: i18n.t('LocalErrors.Digits'),
      },
      min: {
        value: ValidationConstants.minPersonMeasureValue,
        message: i18n.t('LocalErrors.MinValue', { minValue: ValidationConstants.minPersonMeasureValue }),
      },
    },
    maxHeightRule: {
      max: {
        value: ValidationConstants.maxHeightValue,
        message: i18n.t('LocalErrors.MaxValue', { maxValue: ValidationConstants.maxHeightValue }),
      },
    },
    maxWeightRule: {
      max: {
        value: ValidationConstants.maxWeightValue,
        message: i18n.t('LocalErrors.MaxValue', { maxValue: ValidationConstants.maxWeightValue }),
      },
    },
    maxCATRule: {
      max: {
        value: ValidationConstants.maxCATValue,
        message: i18n.t('LocalErrors.MaxValue', { maxValue: ValidationConstants.maxCATValue }),
      },
    },
    minAnalysisQuantityRule: {
      min: {
        value: ValidationConstants.minAnalysisQuantityLimit,
        message: i18n.t('LocalErrors.MinValue', { minValue: ValidationConstants.minAnalysisQuantityLimit }),
      },
    },
    issueDateRule: {
      min: {
        value: convertDateToString(startOfTomorrow())!,
        message: i18n.t('ServerErrors.issue_deadline_to_early'),
      },
    },
    filesValidate: (value: AppAttachmentValue[] | null) => {
      const files = value?.filter((f) => f.file != null && f.file.name != null).map((v) => v.file);
      if (!files) {
        return undefined;
      }

      const errors = [...fileSizeValidation(files as File[]).errors, ...fileTypeValidation(files as File[]).errors];
      return errors.length > 0 ? errors.join('\n\r') : undefined;
    },
    multipleDropdownFieldLengthValidate: (values: string[] | null) => {
      if (!values) {
        return undefined;
      }
      const notValidStr = values.find((x) => {
        return x ? x.length > ValidationConstants.textInputMaxLength : false;
      });
      return notValidStr ? i18n.t('LocalErrors.LengthIsExceeded') : undefined;
    },
    notEmpty: (value: string | undefined) => {
      return value?.trim().length === 0 ? i18n.t('LocalErrors.FieldRequired') : undefined;
    },
    dropDownWithOtherFieldValidate: (val: string | null) => {
      if (!val || val.length < ValidationConstants.textInputMaxLength) {
        return undefined;
      }

      return i18n.t('LocalErrors.LengthIsExceeded');
    },
  };
};
